import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/constants/apiRoutes';
import { queryKeys } from 'src/constants/querykeys';
import { ShareShoppingListRequest, ShareShoppingListResponse } from 'src/types/favorite';
import axiosInstance from 'src/utils/axios';

async function shareShoppingList(
  request: ShareShoppingListRequest
): Promise<ShareShoppingListResponse> {
  const res = await axiosInstance.post<ShareShoppingListResponse>(api.shareShoppingList, {
    folderIds: Array.from(new Set(request.folderIds)),
    folderAddUsers: Array.from(new Set(request.folderAddUsers)),
    folderRemoveUsers: Array.from(new Set(request.folderRemoveUsers)),
  });
  return res.data;
}

interface UseShareShoppingListMutationOptions {
  onSuccess?: (data: ShareShoppingListResponse, variables: ShareShoppingListRequest) => void;
  onError?: (error: unknown) => void;
}

const useShareShoppingListMutation = ({
  onSuccess,
  onError,
}: UseShareShoppingListMutationOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.favorite.shareShoppingList(),
    mutationFn: shareShoppingList,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.favorite.allFolders() });
      queryClient.invalidateQueries({ queryKey: queryKeys.favorite.folders() });
      // Drop cached recipients so Share/Copy reloads selected users when reopened.
      queryClient.removeQueries({ queryKey: [...queryKeys.favorite.root, 'clients'] });
      onSuccess?.(data, variables);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export default useShareShoppingListMutation;
